import React from 'react';
import { Pressable, StyleSheet } from 'react-native';

import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';

type DesktopSideRailChipProps = {
    label: string;
    count?: number;
    accent?: boolean;
    onPress: () => void;
    active?: boolean;
    testID?: string;
};

/**
 * Menu row chip of the desktop web casino side rail.
 */
export const DesktopSideRailChip = ({
    label,
    count,
    accent = false,
    onPress,
    active = false,
    testID,
}: DesktopSideRailChipProps) => {
    const { hovered, hoverHandlers } = useHover();

    const labelColor = active || hovered ? ds.colorToken.text.primary : ds.colorToken.text.secondary;

    return (
        <Pressable
            {...hoverHandlers}
            onPress={onPress}
            style={[styles.chip, active ? styles.chipActive : null, hovered && !active ? styles.chipHovered : null]}
            accessibilityRole="button"
            accessibilityLabel={label}
            accessibilityState={{ selected: active }}
            testID={testID}
        >
            <Text
                variant="labelLarge2"
                style={[styles.label, accent ? styles.labelAccent : null]}
                color={labelColor}
                numberOfLines={1}
            >
                {label}
            </Text>
            {count !== undefined ? (
                <Text variant="labelMedium" color="neutral400">
                    {count}
                </Text>
            ) : null}
        </Pressable>
    );
};

const styles = StyleSheet.create({
    chip: {
        alignItems: 'center',
        borderColor: 'transparent',
        borderRadius: 10,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'space-between',
        paddingHorizontal: 15,
        paddingVertical: 9,
    },
    chipActive: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderColor: ds.colorValue.border.subtle,
    },
    chipHovered: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderColor: ds.colorValue.border.subtle,
    },
    label: {
        flexShrink: 1,
    },
    labelAccent: {
        color: ds.colorValue.background.accent.red.strong,
    },
});
